import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { FlexRow } from 'src/components/Reusable/Flex'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { BasicDropdownProvider } from 'src/components/Reusable/BasicDropdown/context/BasicDropdownContext'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { ControlsAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useIsContentSearchActive } from 'src/Utils/search/search.hooks'
import { AuditScanProgressProvider } from 'src/pages/Roadmap_v2/smartchecks/context/AuditScanProgressContext'
import { GetAuditReadinessRequest } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { Grid } from '../../../../components/Reusable/Grid'
import { ControlResponsibilityModal } from '../../modals/ControlResponsibilityModal'
import { ControlListItemConfig } from '../../IndexItem/ControlListItem.constants'
import { ControlChecksProvider } from '../../IndexItem/ControlChecksContext'
import {
  getControlsListItemTemplateColumns,
  useShowControlRiskColumn,
} from '../../IndexItem/ControlsListItem.helpers'
import { ControlsIndexGridHeader } from './ControlsIndexGridHeader'
import {
  ControlGridRows,
  getControlsGridEmptyState,
} from './ControlsIndexGrid.helpers'

interface ControlsIndexGridProps {
  controls: Control[]
  isLoading?: boolean
  audit?: AuditRecord
  config?: ControlListItemConfig
  // pass this on pages that already fetch readiness so the provider shares that fetch
  controlChecksRequest?: GetAuditReadinessRequest
}

export const ControlsIndexGrid = ({
  audit,
  controls,
  isLoading,
  config,
  controlChecksRequest,
}: ControlsIndexGridProps): JSX.Element => {
  const location = useLocation()
  const isContentSearchActive = useIsContentSearchActive()
  const isMultiPolicyEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.MULTI_POLICY_CONTROL_LINKAGES,
  )
  const policyNamesByControlId = useMemo(() => {
    const map: Record<string, string[]> = {}
    if (!isMultiPolicyEnabled) return map
    controls.forEach((control) => {
      map[control.getId()] = control
        .getLinkedPoliciesList()
        .map((policy) => policy.getPolicyName())
        .filter(Boolean)
    })
    return map
  }, [controls, isMultiPolicyEnabled])
  const departmentNamesByControlId = useMemo(() => {
    const map: Record<string, string[]> = {}
    if (!isMultiPolicyEnabled) return map
    controls.forEach((control) => {
      const uniqueDepartmentNames = new Set(
        control
          .getLinkedPoliciesList()
          .map((policy) => policy.getDepartment())
          .filter(Boolean),
      )
      map[control.getId()] = Array.from(uniqueDepartmentNames)
    })
    return map
  }, [controls, isMultiPolicyEnabled])
  const canViewAuditScans = useCanPerformAction(CAPABILITY.CAN_VIEW_AUDIT_SCANS)
  const showRiskColumn = useShowControlRiskColumn(config)

  if (isLoading) {
    return (
      <FlexRow $mt={40}>
        <TableListLoader rows={20} />
      </FlexRow>
    )
  }

  const controlGridEmptyState = getControlsGridEmptyState()
  const isControlsIndex = location.pathname.includes(
    ControlsAbsoluteRoutes.INDEX,
  )
  const showContentMatchColumn = isContentSearchActive && isControlsIndex
  const templateColumns = getControlsListItemTemplateColumns(
    showContentMatchColumn,
    canViewAuditScans,
    config,
    showRiskColumn,
  )

  return !controls?.length ? (
    <>{controlGridEmptyState}</>
  ) : (
    <BasicDropdownProvider>
      <AuditScanProgressProvider>
        <ControlChecksProvider request={controlChecksRequest}>
          <Grid pb="xs" gridTemplateColumns={templateColumns}>
            <ControlsIndexGridHeader
              config={config}
              showContentMatchColumn={showContentMatchColumn}
            />
            <ControlGridRows
              controls={controls}
              audit={audit}
              config={config}
              policyNamesByControlId={policyNamesByControlId}
              departmentNamesByControlId={departmentNamesByControlId}
            />
          </Grid>
          {/* Modals */}
          <ControlResponsibilityModal />
        </ControlChecksProvider>
      </AuditScanProgressProvider>
    </BasicDropdownProvider>
  )
}
